import React, { ReactNode, useEffect, useState } from 'react';
import { BREAKPOINTS, Button, COLORS, ComponentS, ComponentTextStyle, ComponentXS, MenuItem, Size, SystemIcons, ProfileButton } from '@laerdal/life-react-components';
import styled, { css } from 'styled-components';
import i18next from '../../i18n';
import { Helmet } from 'react-helmet';

export interface UserProps {
  firstName: string;
  lastName: string;
  email: string;
  market?: string;
}

export interface CustomLinkProps {
  elementId?: string,
  icon?: ReactNode;
  iconHtmlString?: string;
  label: string;
  onClick: () => void;
}

export interface CustomContentProps {
  title: string;
  items: CustomLinkProps[];
}

export interface ProfileComponentProps {
  organizationName?: string;
  user: UserProps;
  customContent?: CustomContentProps;
  onSignOut: () => void;
  hideLaguage?: boolean;
  locale: string;
  env: 'dev' | 'test' | 'preprod' | 'prod';
  profileCircleSize?: string;
  profileTextSize?: string;
}

const UserMenu = styled.div<ProfileProps>`
  z-index: 9999;
  background-color: ${COLORS.white};
  display: flex;
  top: 0;
  right: 0;
  flex-direction: column;
  color: black;


  width: 100%;
  height: 100%;
  position: fixed;

  ${BREAKPOINTS.SMALL} {
    height: auto;
    position: absolute;
    top: ${props => props.profileCircleSize ? props.profileCircleSize : '36px'};;
    right: -10px;

    width: 320px;
    margin: 5px 0 0 0;

    border: 1px solid ${COLORS.white};
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15), 0px 0px 6px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
  }
`;

const SignOutButton = styled(Button)`
  width: 240px;
  margin: 20px 24px 20px 0;
  padding: 4px 0 5px 0;
  align-self: end;
`;

const ProfileMenuItem = styled(MenuItem)`
  padding: 0;
`;

const SectionHeader = styled(ComponentXS)`
  margin: 18px 24px 4px 20px;
  text-align: start;
`;

const ProfileCircleWrapper = styled.div`
  margin: 24px 0 0 16px;
  align-self: self-start;
`;

const CloseButton = styled(Button)`
  align-self: end;

  ${BREAKPOINTS.SMALL} {
    display: none;
  }
`;

const NameContainer = styled(ComponentS)`
  overflow-wrap: anywhere;
  font-size: 20px;
`;
const EmailContainer = styled(ComponentXS)`
  overflow-wrap: anywhere;
`;

const Divider = styled.div`
  border-top: 1px solid ${COLORS.neutral_200};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 9px;
`;

const RightRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 9px;
  justify-content: end;
`;

const Wrapper = styled.div`
  font-family: 'Lato', sans-serif;

  button, input, label {
    font-family: inherit;
  }

  ${BREAKPOINTS.SMALL} {
    position: relative;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin: 26px 14px;
  gap: 4px;
`;

const EditProfileButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  margin: 2px 0 0 0;
  gap: 4px;

  &:hover {
    background: none;
    border: none;
  }

  &:active {
    background: none;
    border: none;
  }

  &:focus {
    background: none;
    border: none;
    outline: none;
  }

  &:focus-visible {
    background: none;
    border: none;
  }
`;

interface ProfileProps {
  profileCircleSize?: string;
  profileTextSize?: string;
}

const GenerateTextStyling = (size?: string) => size
  ? css` .initials {
          font-size: ${size} !important;
        }
  `
  : '';
const GenerateProfileCircleStyling = (size?: string) => size
  ? css`
          & > div {
            width: ${size} !important;
            height: ${size} !important;
          }

          & > div > button {
            padding: 0 !important;
            width: ${size} !important;
            height: ${size} !important;
          }

          & div > button > div {
            width: ${size} !important;
            height: ${size} !important;
          }
        }
  `
  : '';
const GenerateProfileStyling = (props: { profileCircleSize?: string, profileTextSize?: string }) => {
  return css`
    ${GenerateTextStyling(props.profileTextSize)}
    ${GenerateProfileCircleStyling(props.profileCircleSize)}
  `;
};

const ProfileButtonWrapper = styled.div<{ profileCircleSize?: string, profileTextSize?: string }>`
  ${props => GenerateProfileStyling(props)}
`;

const MenuProfileCircle = styled.div<ProfileProps>`
  background-color: ${COLORS.primary_500};
  height: 48px;
  width: 48px;
  border-radius: 50%;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  align-items: center;
`;

const MenuProfileText = styled.div<ProfileProps>`
  font-size: 20px;
  font-weight: 700;
  line-height: 20px;
  text-transform: none;
  text-decoration-line: none;
  font-style: normal;
  color: rgb(255, 255, 255);
`;


const useClickOutsideRef = (callback: () => void, relatedRefs: any[] = [], ref?: any) => {
  const elementRef = ref ?? React.useRef<any>(null);

  React.useEffect(() => {
    if (!elementRef.current) return;
    let container = elementRef.current;

    const handleClickOutside = (event: any) => {
      if (container
        && (event.clientX < document.documentElement.offsetWidth) //does not trigger if you click on browser scroll
        && !container.contains(event.target)
        && !relatedRefs.some(ref => ref.current
          && ref.current.contains(event.target))) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };

  }, [elementRef, relatedRefs]);

  return elementRef;
};


export const ProfileComponent = ({ organizationName, user, customContent, onSignOut, hideLaguage, locale, env, profileCircleSize, profileTextSize }: ProfileComponentProps) => {

  useEffect(() => {
    if (!!locale) i18next.changeLanguage(locale.toLowerCase());
    else i18next.changeLanguage('en');
  }, [locale]);

  const [showMenu, setShowMenu] = useState<boolean>(false);

  const wrapperRef = React.useRef<any>(null);

  const containerRef = useClickOutsideRef(() => setShowMenu(false), [wrapperRef]);

  const GetProfileLink = () => {
    if (env === 'dev') return 'https://account-dev.laerdal.com/profile';
    if (env === 'test') return 'https://account-test.laerdal.com/profile';
    if (env === 'preprod') return 'https://account-preprod.laerdal.com/profile';

    return 'https://account.laerdal.com/profile';
  };
  const GetAccountLink = () => {
    if (env === 'dev') return 'https://account-dev.laerdal.com';
    if (env === 'test') return 'https://account-test.laerdal.com';
    if (env === 'preprod') return 'https://account-preprod.laerdal.com';

    return 'https://account.laerdal.com';
  };
  const GetHelpCenterLink = () => {
    let url = 'https://support.laerdal.com';

    if (env === 'dev') url = `https://support-dev.laerdal.com`;
    if (env === 'test') url = `https://support-test.laerdal.com`;
    if (env === 'preprod') url = `https://support-test.laerdal.com`;

    return url;
  };
  const GetLanguageLink = () => {
    if (env === 'dev') return 'https://account-dev.laerdal.com/profile/language-region';
    if (env === 'test') return 'https://account-test.laerdal.com/profile/language-region';
    if (env === 'preprod') return 'https://account-preprod.laerdal.com/profile/language-region';

    return 'https://account.laerdal.com/profile/language-region';
  };

  const GetLanguageName = () => {
    if (!locale) return i18next.t('English');

    if (locale.toLocaleLowerCase().startsWith('af')) return i18next.t('Afrikaans');
    if (locale.toLocaleLowerCase().startsWith('sq')) return i18next.t('Albanian');
    if (locale.toLocaleLowerCase().startsWith('am')) return i18next.t('Amharic');
    if (locale.toLocaleLowerCase().startsWith('ar')) return i18next.t('Arabic');
    if (locale.toLocaleLowerCase().startsWith('hy')) return i18next.t('Armenian');
    if (locale.toLocaleLowerCase().startsWith('as')) return i18next.t('Assamese');
    if (locale.toLocaleLowerCase().startsWith('az')) return i18next.t('Azeri');
    if (locale.toLocaleLowerCase().startsWith('eu')) return i18next.t('Basque');
    if (locale.toLocaleLowerCase().startsWith('be')) return i18next.t('Belarusian');
    if (locale.toLocaleLowerCase().startsWith('bn')) return i18next.t('Bengali');
    if (locale.toLocaleLowerCase().startsWith('bs')) return i18next.t('Bosnian');
    if (locale.toLocaleLowerCase().startsWith('bg')) return i18next.t('Bulgarian');
    if (locale.toLocaleLowerCase().startsWith('my')) return i18next.t('Burmese');
    if (locale.toLocaleLowerCase().startsWith('ca')) return i18next.t('Catalan');
    if (locale.toLocaleLowerCase().startsWith('zh')) return i18next.t('Chinese');
    if (locale.toLocaleLowerCase().startsWith('hr')) return i18next.t('Croatian');
    if (locale.toLocaleLowerCase().startsWith('cs')) return i18next.t('Czech');
    if (locale.toLocaleLowerCase().startsWith('da')) return i18next.t('Danish');
    if (locale.toLocaleLowerCase().startsWith('nl')) return i18next.t('Dutch');
    if (locale.toLocaleLowerCase().startsWith('en')) return i18next.t('English');
    if (locale.toLocaleLowerCase().startsWith('et')) return i18next.t('Estonian');
    if (locale.toLocaleLowerCase().startsWith('fo')) return i18next.t('Faroese');
    if (locale.toLocaleLowerCase().startsWith('fa')) return i18next.t('Farsi');
    if (locale.toLocaleLowerCase().startsWith('fi')) return i18next.t('Finnish');
    if (locale.toLocaleLowerCase().startsWith('fr')) return i18next.t('French');
    if (locale.toLocaleLowerCase().startsWith('gd')) return i18next.t('Gaelic');
    if (locale.toLocaleLowerCase().startsWith('gl')) return i18next.t('Galician');
    if (locale.toLocaleLowerCase().startsWith('ka')) return i18next.t('Georgian');
    if (locale.toLocaleLowerCase().startsWith('de')) return i18next.t('German');
    if (locale.toLocaleLowerCase().startsWith('el')) return i18next.t('Greek');
    if (locale.toLocaleLowerCase().startsWith('gn')) return i18next.t('Guarani');
    if (locale.toLocaleLowerCase().startsWith('gu')) return i18next.t('Gujarati');
    if (locale.toLocaleLowerCase().startsWith('he')) return i18next.t('Hebrew');
    if (locale.toLocaleLowerCase().startsWith('hi')) return i18next.t('Hindi');
    if (locale.toLocaleLowerCase().startsWith('hu')) return i18next.t('Hungarian');
    if (locale.toLocaleLowerCase().startsWith('is')) return i18next.t('Icelandic');
    if (locale.toLocaleLowerCase().startsWith('id')) return i18next.t('Indonesian');
    if (locale.toLocaleLowerCase().startsWith('it')) return i18next.t('Italian');
    if (locale.toLocaleLowerCase().startsWith('ja')) return i18next.t('Japanese');
    if (locale.toLocaleLowerCase().startsWith('kn')) return i18next.t('Kannada');
    if (locale.toLocaleLowerCase().startsWith('ks')) return i18next.t('Kashmiri');
    if (locale.toLocaleLowerCase().startsWith('kk')) return i18next.t('Kazakh');
    if (locale.toLocaleLowerCase().startsWith('km')) return i18next.t('Khmer');
    if (locale.toLocaleLowerCase().startsWith('ko')) return i18next.t('Korean');
    if (locale.toLocaleLowerCase().startsWith('lo')) return i18next.t('Lao');
    if (locale.toLocaleLowerCase().startsWith('la')) return i18next.t('Latin');
    if (locale.toLocaleLowerCase().startsWith('lv')) return i18next.t('Latvian');
    if (locale.toLocaleLowerCase().startsWith('lt')) return i18next.t('Lithuanian');
    if (locale.toLocaleLowerCase().startsWith('ms')) return i18next.t('Malay');
    if (locale.toLocaleLowerCase().startsWith('ml')) return i18next.t('Malayalam');
    if (locale.toLocaleLowerCase().startsWith('mt')) return i18next.t('Maltese');
    if (locale.toLocaleLowerCase().startsWith('mi')) return i18next.t('Maori');
    if (locale.toLocaleLowerCase().startsWith('mr')) return i18next.t('Marathi');
    if (locale.toLocaleLowerCase().startsWith('mn')) return i18next.t('Mongolian');
    if (locale.toLocaleLowerCase().startsWith('mn')) return i18next.t('Mongolian');
    if (locale.toLocaleLowerCase().startsWith('ne')) return i18next.t('Nepali');
    if (locale.toLocaleLowerCase().startsWith('nb')) return i18next.t('Norwegian');
    if (locale.toLocaleLowerCase().startsWith('or')) return i18next.t('Oriya');
    if (locale.toLocaleLowerCase().startsWith('pl')) return i18next.t('Polish');
    if (locale.toLocaleLowerCase().startsWith('pt')) return i18next.t('Portuguese');
    if (locale.toLocaleLowerCase().startsWith('pa')) return i18next.t('Punjabi');
    if (locale.toLocaleLowerCase().startsWith('rm')) return i18next.t('Raeto');
    if (locale.toLocaleLowerCase().startsWith('ro')) return i18next.t('Romanian');
    if (locale.toLocaleLowerCase().startsWith('ru')) return i18next.t('Russian');
    if (locale.toLocaleLowerCase().startsWith('ru')) return i18next.t('Russian');
    if (locale.toLocaleLowerCase().startsWith('sa')) return i18next.t('Sanskrit');
    if (locale.toLocaleLowerCase().startsWith('sr')) return i18next.t('Serbian');
    if (locale.toLocaleLowerCase().startsWith('tn')) return i18next.t('Setsuana');
    if (locale.toLocaleLowerCase().startsWith('sd')) return i18next.t('Sindhi');
    if (locale.toLocaleLowerCase().startsWith('sk')) return i18next.t('Slovak');
    if (locale.toLocaleLowerCase().startsWith('sl')) return i18next.t('Slovenian');
    if (locale.toLocaleLowerCase().startsWith('so')) return i18next.t('Somali');
    if (locale.toLocaleLowerCase().startsWith('sb')) return i18next.t('Sorbian');
    if (locale.toLocaleLowerCase().startsWith('es')) return i18next.t('Spanish');
    if (locale.toLocaleLowerCase().startsWith('sw')) return i18next.t('Swahili');
    if (locale.toLocaleLowerCase().startsWith('sv')) return i18next.t('Swedish');
    if (locale.toLocaleLowerCase().startsWith('tg')) return i18next.t('Tajik');
    if (locale.toLocaleLowerCase().startsWith('ta')) return i18next.t('Tamil');
    if (locale.toLocaleLowerCase().startsWith('tt')) return i18next.t('Tatar');
    if (locale.toLocaleLowerCase().startsWith('te')) return i18next.t('Telugu');
    if (locale.toLocaleLowerCase().startsWith('th')) return i18next.t('Thai');
    if (locale.toLocaleLowerCase().startsWith('bo')) return i18next.t('Tibetan');
    if (locale.toLocaleLowerCase().startsWith('ts')) return i18next.t('Tsonga');
    if (locale.toLocaleLowerCase().startsWith('tr')) return i18next.t('Turkish');
    if (locale.toLocaleLowerCase().startsWith('tk')) return i18next.t('Turkmen');
    if (locale.toLocaleLowerCase().startsWith('uk')) return i18next.t('Ukrainian');
    if (locale.toLocaleLowerCase().startsWith('ur')) return i18next.t('Urdu');
    if (locale.toLocaleLowerCase().startsWith('uz')) return i18next.t('Uzbek');
    if (locale.toLocaleLowerCase().startsWith('vi')) return i18next.t('Vietnamese');
    if (locale.toLocaleLowerCase().startsWith('cy')) return i18next.t('Welsh');
    if (locale.toLocaleLowerCase().startsWith('xh')) return i18next.t('Xhosa');
    if (locale.toLocaleLowerCase().startsWith('yi')) return i18next.t('Yiddish');
    if (locale.toLocaleLowerCase().startsWith('zu')) return i18next.t('Zulu');

    return i18next.t('English');
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Helmet>
        <link href='https://cdn1.laerdal.com/webfonts/fonts.css' rel='stylesheet'></link>
      </Helmet>
      <ProfileButtonWrapper profileCircleSize={profileCircleSize} profileTextSize={profileTextSize}>
        <ProfileButton onClick={() => setShowMenu(!showMenu)}
          initials={(user?.firstName[0]?.toUpperCase() ?? '') + (user?.lastName[0]?.toUpperCase() ?? '')}
          id='profileMenu' />
      </ProfileButtonWrapper>
      {showMenu &&
        <UserMenu ref={containerRef} profileCircleSize={profileCircleSize}>
          <CloseButton icon={<SystemIcons.Close />} variant='tertiary' onClick={() => setShowMenu(!showMenu)} />
          <Row>
            <ProfileCircleWrapper>
              <MenuProfileCircle>
                <MenuProfileText>
                  {(user?.firstName[0]?.toUpperCase() ?? '') + (user?.lastName[0]?.toUpperCase() ?? '')}
                </MenuProfileText>
              </MenuProfileCircle>
            </ProfileCircleWrapper>
            <UserDetails>
              <NameContainer textStyle={ComponentTextStyle.Bold} color={COLORS.neutral_600}>{user.firstName} {user.lastName}</NameContainer>
              <EmailContainer textStyle={ComponentTextStyle.Regular} color={COLORS.neutral_600}>{user.email}</EmailContainer>
              <EditProfileButton onClick={() => {
                //@ts-ignore
                if (window.posthog) {
                  //@ts-ignore
                  window.posthog.capture('NavigationUserProfile EditProfile');
                }
                //location.href = GetProfileLink();
                window.open(GetProfileLink(), '_blank');
              }}>
                <ComponentXS textStyle={ComponentTextStyle.Bold} color={COLORS.primary_600}>{i18next.t('Edit profile')} </ComponentXS>

                <SystemIcons.OpenNewWindow size='16px' color={COLORS.primary_600} />
              </EditProfileButton>
            </UserDetails>
          </Row>


          {!!organizationName && <Divider />}
          {!!organizationName && <SectionHeader color={COLORS.neutral_400} textStyle={ComponentTextStyle.Bold}>{organizationName}</SectionHeader>}
          {!!organizationName &&
            <ProfileMenuItem
              item={{ value: 'Account', displayLabel: i18next.t('Account'), icon: <SystemIcons.Team size='24px' /> }}
              active={false}
              id='Account'
              key='Account'
              size={Size.Medium}
              onClickHandler={() => {
                //@ts-ignore
                if (window.posthog) {
                  //@ts-ignore
                  window.posthog.capture('NavigationUserProfile Account');
                }
                location.href = GetAccountLink();
              }
              } />
          }


          {!!customContent && customContent.items.length > 0 &&
            <>
              <Divider />
              <SectionHeader color={COLORS.neutral_400} textStyle={ComponentTextStyle.Bold}>{customContent.title}</SectionHeader>
              {customContent.items.map((custom, i) =>
                <ProfileMenuItem
                  item={{
                    value: custom.label,
                    displayLabel: custom.label,
                    //@ts-ignore
                    icon: !!custom.iconHtmlString ? <div key={'div' + custom.label + i} dangerouslySetInnerHTML={{ __html: custom.iconHtmlString }}></div> : custom.icon
                  }}

                  active={false}
                  id={!!custom.elementId ? custom.elementId : custom.label}
                  key={custom.label + i}
                  size={Size.Medium}
                  onMouseDownCapture={(e) => {
                    if(e.button == 1) {
                      setShowMenu(false);
                      custom.onClick();
                    }
                  }}
                  onClickHandler={() => {
                    setShowMenu(false);
                    custom.onClick();
                  }
                  } />
              )
              }
            </>
          }
          <Divider />
          <ProfileMenuItem
            item={{ value: 'HelpCenter', displayLabel: i18next.t('Help Center'), icon: <SystemIcons.Support size='24px' /> }}
            active={false}
            id='HelpCenter'
            key='HelpCenter'
            size={Size.Medium}
            onMouseDownCapture={(e) => {
              if(e.button == 1) {
                window.open(GetHelpCenterLink(), '_blank');
              }
            }}
            onClickHandler={() => {
              //@ts-ignore
              if (window.posthog) {
                //@ts-ignore
                window.posthog.capture('NavigationUserProfile HelpCenter');
              }
              window.open(GetHelpCenterLink(), '_blank');
            }} />
          <Divider />
          <RightRow>

            <ProfileMenuItem
              style={hideLaguage ? { visibility: "hidden" } : { display: "flex" }}
              item={{ value: GetLanguageName(), displayLabel: GetLanguageName(), icon: <SystemIcons.Language size='24px' /> }}
              active={false}
              id='language'
              key='language'
              size={Size.Medium}
              onMouseDownCapture={(e) => {
                if(e.button == 1) {
                  setShowMenu(false);
                  window.open(GetLanguageLink(), '_blank');
                }
              }}
              onClickHandler={() => {
                //@ts-ignore
                if (window.posthog) {
                  //@ts-ignore
                  window.posthog.capture('NavigationUserProfile Change Language');
                }
                location.href = GetLanguageLink();
              }} />

            <SignOutButton
              testId='profileComponentSignoutButton'
              size={Size.Medium}
              variant='secondary'
              onClick={() => {
                setShowMenu(false);
                if (!!onSignOut) onSignOut();
              }}>
              {i18next.t('Sign out')}
            </SignOutButton>
          </RightRow>
        </UserMenu>
      }
    </Wrapper>
  );
};
